import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Info } from 'lucide-react';

const EN = {
  heading: 'Function and limitations of use',
  paragraphs: [
    'MyGuardPod products are protective carrying cases intended to contain, organize, identify and transport compatible emergency-medication containers and supplies. Medication and medical supplies are not included.',
    'The cases do not administer medication, control temperature, extend shelf life, or guarantee the integrity, storage conditions or effectiveness of any medication. They are not sterile, child-resistant, waterproof or submersible.',
    'Always follow the prescription label, the medication manufacturer\u2019s storage instructions, your healthcare professional\u2019s emergency plan and applicable local requirements. Inspect the case regularly and replace it if damaged. Do not use a case that compresses its contents or prevents proper inspection of the medication or supplies.',
  ],
};

const FR = {
  heading: 'Fonction et limites d\u2019utilisation',
  paragraphs: [
    'Les produits MyGuardPod sont des \u00e9tuis protecteurs destin\u00e9s \u00e0 contenir, organiser, identifier et transporter des contenants de m\u00e9dicaments d\u2019urgence et du mat\u00e9riel compatibles. M\u00e9dicament et mat\u00e9riel m\u00e9dical non inclus.',
    'Les \u00e9tuis n\u2019administrent aucun m\u00e9dicament, ne contr\u00f4lent pas la temp\u00e9rature, ne prolongent pas la dur\u00e9e de conservation et ne garantissent ni l\u2019int\u00e9grit\u00e9, ni les conditions de conservation, ni l\u2019efficacit\u00e9 d\u2019un m\u00e9dicament. Ils ne sont ni st\u00e9riles, ni \u00e0 l\u2019\u00e9preuve des enfants, ni \u00e9tanches, ni submersibles.',
    'Respectez toujours l\u2019\u00e9tiquette de prescription, les consignes de conservation du fabricant du m\u00e9dicament, le plan d\u2019urgence \u00e9tabli avec votre professionnel de la sant\u00e9 et les exigences locales applicables. Inspectez r\u00e9guli\u00e8rement l\u2019\u00e9tui et remplacez-le s\u2019il est endommag\u00e9. N\u2019utilisez pas un \u00e9tui qui comprime son contenu ou emp\u00eache l\u2019inspection ad\u00e9quate du m\u00e9dicament ou du mat\u00e9riel.',
  ],
};

/**
 * Compact, page-wide legal / limitations notice.
 * Use `variant="dark"` on dark backgrounds; default is light card.
 */
export const ProductLimitationsNotice = ({ variant = 'light' }) => {
  const { language } = useLanguage();
  const c = language === 'fr' ? FR : EN;
  const isDark = variant === 'dark';
  return (
    <aside
      data-testid="product-limitations-notice"
      className={`rounded-2xl p-6 sm:p-8 border ${
        isDark
          ? 'bg-neutral-900 border-neutral-800 text-neutral-300'
          : 'bg-neutral-50 border-neutral-200 text-neutral-700'
      }`}
    >
      <div className="flex items-start gap-3 mb-3">
        <Info className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isDark ? 'text-[#E31B23]' : 'text-[#E31B23]'}`} />
        <h3 className={`font-outfit font-bold text-base sm:text-lg ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
          {c.heading}
        </h3>
      </div>
      <div className="space-y-3 pl-8">
        {c.paragraphs.map((p, i) => (
          <p key={i} className="font-manrope text-sm leading-relaxed">
            {p}
          </p>
        ))}
      </div>
    </aside>
  );
};

/**
 * Ultra-short one-liner disclaimer for placement near Etsy CTAs.
 */
export const ShortLimitationsDisclaimer = ({ className = '' }) => {
  const { language } = useLanguage();
  const text =
    language === 'fr'
      ? '\u00c9tui protecteur seulement. M\u00e9dicament non inclus. Aucun contr\u00f4le de temp\u00e9rature. Respectez les instructions du fabricant du m\u00e9dicament.'
      : 'Protective carrying case only. Medication not included. No temperature control. Follow the medication manufacturer\u2019s instructions.';
  return (
    <p
      data-testid="short-limitations-disclaimer"
      className={`font-manrope text-xs text-neutral-500 leading-relaxed ${className}`}
    >
      {text}
    </p>
  );
};

export default ProductLimitationsNotice;
